import { FilterGroup } from '@fe/core';
import { WeatherSettingsChangeSource } from '@fe/weather';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useEffect } from 'react';
import { useAppDispatch } from '../../../../app/hooks/store-hooks';
import useFilterGroup from '../../../../hooks/use-filter-group';
import { FilterGroupType } from '../../../../types/filter-group';
import SettingsSubSection from '../../../common/SettingsSubSection';
import useWeatherService from '../../../hooks/use-weather-service';
import {
    FlightCategoryOptions,
    MetarDisplayModeOptions,
    MinimumCeilingOptions,
    MinimumFaaDelayOptions,
    MinimumRvrOptions,
    MinimumVisibilityOptions
} from '../settings-options';
import { useBooleanSetting, useDropdownSetting } from './hooks/useSetting';
import { patchFocusedWorkspaceView } from '../../../../app/state/store';
import { weatherSettingsToUdView } from '../../../../udView/weather-settings-from-view';

const nullFilterGroup = {
    name: 'None',
    id: null
};

export default function AirportWeatherSettings() {
    const weatherService = useWeatherService();
    const airportFilterGroups = useFilterGroup(FilterGroupType.airport);
    // prettier-ignore
    const displayMode = useDropdownSetting(MetarDisplayModeOptions, 'airport', 'displayMode', WeatherSettingsChangeSource.airportMetarTafFilter);
    // prettier-ignore
    const minimumCategory = useDropdownSetting(FlightCategoryOptions, 'airport', 'minimumCategory', WeatherSettingsChangeSource.airportMetarTafFilter);
    // prettier-ignore
    const minimumCeiling = useDropdownSetting(MinimumCeilingOptions, 'airport', 'minimumCeiling', WeatherSettingsChangeSource.airportMetarTafFilter);
    // prettier-ignore
    const minimumVisibility = useDropdownSetting(MinimumVisibilityOptions, 'airport', 'minimumVisibility', WeatherSettingsChangeSource.airportMetarTafFilter);
    // prettier-ignore
    const showMetarInColor = useBooleanSetting('airport', 'showMetarDetailInColor', WeatherSettingsChangeSource.airportMetarTafDisplay);
    // prettier-ignore
    const minimumDelay = useDropdownSetting(MinimumFaaDelayOptions, 'airport', 'faaDelayMinimum', WeatherSettingsChangeSource.airportDelays);
    // prettier-ignore
    const minimumRvr = useDropdownSetting(MinimumRvrOptions, 'airport', 'rvrMinimum', WeatherSettingsChangeSource.airportRvr);
    const myAirportsFilter = useBooleanSetting('airport', 'myFleetAirports', WeatherSettingsChangeSource.airportMyAirports);
    const dispatch = useAppDispatch();

    const [filterGroup, setFilterGroup] = React.useState<FilterGroup | null>(null);

    useEffect(() => {
        setFilterGroup(airportFilterGroups.find((x) => x.id === weatherService.getSettings().airport.filterGroup) ?? null);
    }, [weatherService, airportFilterGroups]);

    const handleFilterGroupChanged = React.useCallback(
        (e: DropDownListChangeEvent) => {
            setFilterGroup(e.value);
            weatherService.updateSettings(
                (settings) => (settings.airport.filterGroup = e.value.id),
                WeatherSettingsChangeSource.airportFilterGroup
            );
            dispatch(patchFocusedWorkspaceView((x) => (x.weather.settings = weatherSettingsToUdView(weatherService.getSettings()))));
        },
        [weatherService]
    );

    return (
        <div className="d-flex gap-2 flex-column">
            <SettingsSubSection title="METAR/TAF">
                <div>
                    <div>Display mode</div>
                    <DropDownList {...displayMode} textField="text" dataItemKey="value" />
                </div>
                <div>
                    <Checkbox label="Show METAR in color" {...showMetarInColor} className="my-2"></Checkbox>
                </div>

                <div>
                    <div>Flight category minimum level</div>
                    <DropDownList {...minimumCategory} textField="text" dataItemKey="value" />
                </div>

                <div>
                    <div>Ceiling minimum level</div>
                    <DropDownList {...minimumCeiling} textField="text" dataItemKey="value" />
                </div>

                <div>
                    <div>Visibility minimum level</div>
                    <DropDownList {...minimumVisibility} textField="text" dataItemKey="value" />
                </div>
            </SettingsSubSection>

            <SettingsSubSection title="FAA Delays">
                <div>
                    <div>Minimum level to display</div>
                    <DropDownList {...minimumDelay} textField="text" dataItemKey="value" />
                </div>
            </SettingsSubSection>

            <SettingsSubSection title="RVR">
                <div>
                    <div>Minimum level to display</div>
                    <DropDownList {...minimumRvr} textField="text" dataItemKey="value" />
                </div>
            </SettingsSubSection>

            <SettingsSubSection title="Filter">
                <div>
                    <div>Airport product filters</div>
                    <DropDownList
                        value={filterGroup}
                        data={airportFilterGroups}
                        textField="name"
                        dataItemKey="id"
                        defaultItem={nullFilterGroup}
                        onChange={handleFilterGroupChanged}
                    />
                </div>

                <Checkbox {...myAirportsFilter} label="My Airports" />
            </SettingsSubSection>
        </div>
    );
}
